/**
 * Admin-facing DTOs and Zod schemas for the trade-account application review
 * queue. All wire fields are snake_case per L1 (admin endpoints mirror DB
 * column names for consistency with existing admin controllers).
 *
 * For the applicant-facing (storefront) types see `./application.ts`.
 */
import { z } from "zod";
import { APPLICATION_STATUSES } from "./application.js";
// ---------- Request schemas ----------
/**
 * Body for `POST /v1/admin/applications/:id/approve`. The reviewing staff
 * member must assign a tier and at least one allowed payment method before
 * the application transitions to "approved".
 */
export const ApproveApplicationInput = z.object({
    tier: z.enum(["bronze", "silver", "gold"]),
    allowed_payment_methods: z.array(z.enum(["card", "bank_transfer"])).min(1),
});
/**
 * Body for `POST /v1/admin/applications/:id/reject`. A meaningful rejection
 * reason is required (10–1000 chars) so the applicant can understand the
 * outcome and potentially re-apply with corrected information.
 */
export const RejectApplicationInput = z.object({
    rejection_reason: z.string().min(10).max(1000),
});
/**
 * Query-string parameters for `GET /v1/admin/applications`. Supports optional
 * status filter, free-text search, and cursor-based pagination. `limit`
 * defaults to 20, capped at 50 to prevent oversized admin list payloads.
 */
export const ListApplicationsQuery = z.object({
    status: z.enum(APPLICATION_STATUSES).optional(),
    q: z.string().max(100).optional(),
    cursor: z.string().uuid().optional(),
    limit: z.coerce.number().int().min(1).max(50).default(20),
});
